/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from "react";

import { useLayout } from "@/../_metronic/layout/core";
import { useCurrentAccount } from "@/hooks/useCurrentAccount";
import { Paginated } from "@/@types/generics/paginated";
import { useDebounce } from "@/hooks/useDebounce";
import {
	getResumeIndicatorC7,
	ResumeIndicatorC7Data,
} from "@/services/esus/indicator/esf/c7/getResumeIndicatorC7";
import {
	getResumeIndicatorC7ByHealthTeam,
	ResumeIndicatorC7ByHealthTeamData,
} from "@/services/esus/indicator/esf/c7/getResumeIndicatorC7ByHealthTeam";
import {
	DetailedCitizenAttendancesIndicatorC7Data,
	getDetailedCitizensAttendancesIndicatorC7,
} from "@/services/esus/indicator/esf/c7/getDetailedCitizensAttendancesIndicatorC7";
import {
	DetailedCitizenVaccinationsIndicatorC7Data,
	getDetailedCitizensVaccinationsIndicatorC7,
} from "@/services/esus/indicator/esf/c7/getDetailedCitizensVaccinationsIndicatorC7";
import {
	DetailedCitizenCervicalCancerProceduresIndicatorC7Data,
	getDetailedCitizensCervicalCancerProceduresIndicatorC7,
} from "@/services/esus/indicator/esf/c7/getDetailedCitizensCervicalCancerProceduresIndicatorC7";
import {
	DetailedCitizenBreastCancerProceduresIndicatorC7Data,
	getDetailedCitizensBreastCancerProceduresIndicatorC7,
} from "@/services/esus/indicator/esf/c7/getDetailedCitizensBreastCancerProceduresIndicatorC7";

import { IndicatorEsfC7Resume } from "./IndicatorEsfC7Resume";
import { IndicatorEsfC7CitizensAttendances } from "./IndicatorEsfC7CitizensAttendances";
import { IndicatorEsfC7HealthTeams } from "./IndicatorEsfC7HealthTeams";
import { IndicatorEsfC7CitizensVaccinations } from "./IndicatorEsfC7CitizensVaccinations";
import { IndicatorEsfC7CitizensCervicalCancerProcedures } from "./IndicatorEsfC7CitizensCervicalCancerProcedures";
import { IndicatorEsfC7CitizensBreastCancerProcedures } from "./IndicatorEsfC7CitizensBreastCancerProcedures";

type IndicatorEsfC7Props = {
	selectedQuarter: string;
	selectedYear: number;
	selectedMonth: number;
	selectedIne?: string;
	isPendingToUpdate: boolean;
	setIsPendingToUpdate: (isPendingToUpdate: boolean) => void;
};

export function IndicatorEsfC7({
	selectedQuarter,
	selectedYear,
	selectedMonth,
	selectedIne,
	isPendingToUpdate,
	setIsPendingToUpdate,
}: Readonly<IndicatorEsfC7Props>) {
	const { setTitle } = useLayout();
	const { uf, ibgeCode } = useCurrentAccount();
	const pageSize = 10;
	const [resumeIndicatorC7, setResumeIndicatorC7] = useState<ResumeIndicatorC7Data>(
		new ResumeIndicatorC7Data()
	);

	const [pageNumberHealthTeamsClassifications, setPageNumberHealthTeamsClassifications] =
		useState(1);
	const [searchHealthTeamsClassification, setSearchHealthTeamsClassification] = useState("");
	const debouncedSearchHealthTeamsClassifications = useDebounce(
		searchHealthTeamsClassification,
		2000
	);
	const [paginatedHealthTeamsClassifications, setPaginatedHealthTeamsClassifications] = useState(
		new Paginated<ResumeIndicatorC7ByHealthTeamData>()
	);
	const [isLoadingHealthTeamsClassifications, setIsLoadingHealthTeamsClassifications] =
		useState(false);

	const [
		pageNumberCitizensCervicalCancerProcedures,
		setPageNumberCitizensCervicalCancerProcedures,
	] = useState(1);
	const [searchCitizensCervicalCancerProcedure, setSearchCitizensCervicalCancerProcedure] =
		useState("");
	const [
		isEvaluatedCitizensCervicalCancerProcedure,
		setIsEvaluatedCitizensCervicalCancerProcedure,
	] = useState<boolean>();
	const debouncedSearchCitizensCervicalCancerProcedure = useDebounce(
		searchCitizensCervicalCancerProcedure,
		2000
	);
	const [
		paginatedCitizensCervicalCancerProcedures,
		setPaginatedCitizensCervicalCancerProcedures,
	] = useState(new Paginated<DetailedCitizenCervicalCancerProceduresIndicatorC7Data>());
	const [
		isLoadingCitizensCervicalCancerProcedures,
		setIsLoadingCitizensCervicalCancerProcedures,
	] = useState(false);

	const [pageNumberCitizensVaccinations, setPageNumberCitizensVaccinations] = useState(1);
	const [searchCitizensVaccination, setSearchCitizensVaccination] = useState("");
	const [isEvaluatedCitizensVaccination, setIsEvaluatedCitizensVaccination] = useState<boolean>();
	const debouncedSearchCitizensVaccination = useDebounce(searchCitizensVaccination, 2000);
	const [paginatedCitizensVaccinations, setPaginatedCitizensVaccinations] = useState(
		new Paginated<DetailedCitizenVaccinationsIndicatorC7Data>()
	);
	const [isLoadingCitizensVaccinations, setIsLoadingCitizensVaccinations] = useState(false);

	const [pageNumberAttendances, setPageNumberAttendances] = useState(1);
	const [searchCitizensWithAttendance, setSearchCitizensWithAttendance] = useState("");
	const [isEvaluatedCitizensWithAttendance, setIsEvaluatedCitizensWithAttendance] =
		useState<boolean>();
	const debouncedSearchAttendance = useDebounce(searchCitizensWithAttendance, 2000);
	const [paginatedCitizensWithAttendances, setPaginatedCitizensWithAttendances] = useState(
		new Paginated<DetailedCitizenAttendancesIndicatorC7Data>()
	);
	const [isLoadingAttendances, setIsLoadingAttendances] = useState(false);

	const [pageNumberCitizensBreastCancerProcedures, setPageNumberCitizensBreastCancerProcedures] =
		useState(1);
	const [searchCitizensBreastCancerProcedure, setSearchCitizensBreastCancerProcedure] =
		useState("");
	const [isEvaluatedCitizensBreastCancerProcedure, setIsEvaluatedCitizensBreastCancerProcedure] =
		useState<boolean>();
	const debouncedSearchCitizensBreastCancerProcedure = useDebounce(
		searchCitizensBreastCancerProcedure,
		2000
	);
	const [paginatedCitizensBreastCancerProcedures, setPaginatedCitizensBreastCancerProcedures] =
		useState(new Paginated<DetailedCitizenBreastCancerProceduresIndicatorC7Data>());
	const [isLoadingCitizensBreastCancerProcedures, setIsLoadingCitizensBreastCancerProcedures] =
		useState(false);

	async function handleFilter() {
		await Promise.all([
			handleGetResumeIndicatorC7(),
			handleGetResumeIndicatorC7ByHealthTeam(),
			handleGetDetailedCitizensCervicalCancerProceduresIndicatorC7(),
			handleGetDetailedCitizensVaccinationsIndicatorC7(),
			handleGetDetailedCitizensWithAttendancesIndicatorC7(),
			handleGetDetailedCitizensBreastCancerProceduresIndicatorC7(),
		]);
		setIsPendingToUpdate(false);
	}

	async function handleGetResumeIndicatorC7() {
		setResumeIndicatorC7(
			await getResumeIndicatorC7({
				uf,
				ibgeCode,
				quarter: selectedQuarter,
				year: selectedYear,
				month: selectedMonth,
				ine: selectedIne,
			})
		);
	}

	async function handleGetResumeIndicatorC7ByHealthTeam(pageNumber?: number) {
		setIsLoadingHealthTeamsClassifications(true);
		setPaginatedHealthTeamsClassifications(
			await getResumeIndicatorC7ByHealthTeam({
				uf,
				ibgeCode,
				quarter: selectedQuarter,
				year: selectedYear,
				month: selectedMonth,
				ine: selectedIne,
				search: searchHealthTeamsClassification,
				pageNumber: pageNumber ?? 1,
				pageSize,
			})
		);
		setIsLoadingHealthTeamsClassifications(false);
	}

	async function handleGetAllResumeIndicatorC7ByHealthTeam() {
		return (
			await getResumeIndicatorC7ByHealthTeam({
				uf,
				ibgeCode,
				quarter: selectedQuarter,
				year: selectedYear,
				month: selectedMonth,
				ine: selectedIne,
				search: searchHealthTeamsClassification,
				export: true,
			})
		).data;
	}

	async function handleGetDetailedCitizensCervicalCancerProceduresIndicatorC7(
		pageNumber?: number
	) {
		setIsLoadingCitizensCervicalCancerProcedures(true);
		setPaginatedCitizensCervicalCancerProcedures(
			await getDetailedCitizensCervicalCancerProceduresIndicatorC7({
				uf,
				ibgeCode,
				quarter: selectedQuarter,
				year: selectedYear,
				month: selectedMonth,
				ine: selectedIne,
				search: searchCitizensCervicalCancerProcedure,
				isEvaluated: isEvaluatedCitizensCervicalCancerProcedure,
				pageNumber: pageNumber ?? 1,
				pageSize,
			})
		);
		setIsLoadingCitizensCervicalCancerProcedures(false);
	}

	async function handleGetAllDetailedCitizensCervicalCancerProceduresIndicatorC7() {
		return (
			await getDetailedCitizensCervicalCancerProceduresIndicatorC7({
				uf,
				ibgeCode,
				quarter: selectedQuarter,
				year: selectedYear,
				month: selectedMonth,
				ine: selectedIne,
				search: searchCitizensCervicalCancerProcedure,
				isEvaluated: isEvaluatedCitizensCervicalCancerProcedure,
				export: true,
			})
		).data;
	}

	async function handleGetDetailedCitizensVaccinationsIndicatorC7(pageNumber?: number) {
		setIsLoadingCitizensVaccinations(true);
		setPaginatedCitizensVaccinations(
			await getDetailedCitizensVaccinationsIndicatorC7({
				uf,
				ibgeCode,
				quarter: selectedQuarter,
				year: selectedYear,
				month: selectedMonth,
				ine: selectedIne,
				search: searchCitizensVaccination,
				isEvaluated: isEvaluatedCitizensVaccination,
				pageNumber: pageNumber ?? 1,
				pageSize,
			})
		);
		setIsLoadingCitizensVaccinations(false);
	}

	async function handleGetAllDetailedCitizensVaccinationsIndicatorC7() {
		return (
			await getDetailedCitizensVaccinationsIndicatorC7({
				uf,
				ibgeCode,
				quarter: selectedQuarter,
				year: selectedYear,
				month: selectedMonth,
				ine: selectedIne,
				search: searchCitizensVaccination,
				isEvaluated: isEvaluatedCitizensVaccination,
				export: true,
			})
		).data;
	}

	async function handleGetDetailedCitizensWithAttendancesIndicatorC7(pageNumber?: number) {
		setIsLoadingAttendances(true);
		setPaginatedCitizensWithAttendances(
			await getDetailedCitizensAttendancesIndicatorC7({
				uf,
				ibgeCode,
				quarter: selectedQuarter,
				year: selectedYear,
				month: selectedMonth,
				ine: selectedIne,
				search: searchCitizensWithAttendance,
				isEvaluated: isEvaluatedCitizensWithAttendance,
				pageNumber: pageNumber ?? 1,
				pageSize,
			})
		);
		setIsLoadingAttendances(false);
	}

	async function handleGetAllDetailedCitizensWithAttendancesIndicatorC7() {
		return (
			await getDetailedCitizensAttendancesIndicatorC7({
				uf,
				ibgeCode,
				quarter: selectedQuarter,
				year: selectedYear,
				month: selectedMonth,
				ine: selectedIne,
				search: searchCitizensWithAttendance,
				isEvaluated: isEvaluatedCitizensWithAttendance,
				export: true,
			})
		).data;
	}

	async function handleGetDetailedCitizensBreastCancerProceduresIndicatorC7(pageNumber?: number) {
		setIsLoadingCitizensBreastCancerProcedures(true);
		setPaginatedCitizensBreastCancerProcedures(
			await getDetailedCitizensBreastCancerProceduresIndicatorC7({
				uf,
				ibgeCode,
				quarter: selectedQuarter,
				year: selectedYear,
				month: selectedMonth,
				ine: selectedIne,
				search: searchCitizensBreastCancerProcedure,
				isEvaluated: isEvaluatedCitizensBreastCancerProcedure,
				pageNumber: pageNumber ?? 1,
				pageSize,
			})
		);
		setIsLoadingCitizensBreastCancerProcedures(false);
	}

	async function handleGetAllDetailedCitizensBreastCancerProceduresIndicatorC7() {
		return (
			await getDetailedCitizensBreastCancerProceduresIndicatorC7({
				uf,
				ibgeCode,
				quarter: selectedQuarter,
				year: selectedYear,
				month: selectedMonth,
				ine: selectedIne,
				search: searchCitizensBreastCancerProcedure,
				isEvaluated: isEvaluatedCitizensBreastCancerProcedure,
				export: true,
			})
		).data;
	}

	useEffect(() => {
		if (!isPendingToUpdate)
			handleGetResumeIndicatorC7ByHealthTeam(pageNumberHealthTeamsClassifications);
	}, [pageNumberHealthTeamsClassifications]);

	useEffect(() => {
		if (!isPendingToUpdate) {
			if (pageNumberHealthTeamsClassifications === 1) {
				handleGetResumeIndicatorC7ByHealthTeam(pageNumberHealthTeamsClassifications);
			} else {
				setPageNumberHealthTeamsClassifications(1);
			}
		}
	}, [debouncedSearchHealthTeamsClassifications]);

	useEffect(() => {
		if (!isPendingToUpdate)
			handleGetDetailedCitizensCervicalCancerProceduresIndicatorC7(
				pageNumberCitizensCervicalCancerProcedures
			);
	}, [pageNumberCitizensCervicalCancerProcedures]);

	useEffect(() => {
		if (!isPendingToUpdate) {
			if (pageNumberCitizensCervicalCancerProcedures === 1) {
				handleGetDetailedCitizensCervicalCancerProceduresIndicatorC7(
					pageNumberCitizensCervicalCancerProcedures
				);
			} else {
				setPageNumberCitizensCervicalCancerProcedures(1);
			}
		}
	}, [
		debouncedSearchCitizensCervicalCancerProcedure,
		isEvaluatedCitizensCervicalCancerProcedure,
	]);

	useEffect(() => {
		if (!isPendingToUpdate)
			handleGetDetailedCitizensVaccinationsIndicatorC7(pageNumberCitizensVaccinations);
	}, [pageNumberCitizensVaccinations]);

	useEffect(() => {
		if (!isPendingToUpdate) {
			if (pageNumberCitizensVaccinations === 1) {
				handleGetDetailedCitizensVaccinationsIndicatorC7(pageNumberCitizensVaccinations);
			} else {
				setPageNumberCitizensVaccinations(1);
			}
		}
	}, [debouncedSearchCitizensVaccination, isEvaluatedCitizensVaccination]);

	useEffect(() => {
		if (!isPendingToUpdate)
			handleGetDetailedCitizensWithAttendancesIndicatorC7(pageNumberAttendances);
	}, [pageNumberAttendances]);

	useEffect(() => {
		if (!isPendingToUpdate) {
			if (pageNumberAttendances === 1) {
				handleGetDetailedCitizensWithAttendancesIndicatorC7(pageNumberAttendances);
			} else {
				setPageNumberAttendances(1);
			}
		}
	}, [debouncedSearchAttendance, isEvaluatedCitizensWithAttendance]);

	useEffect(() => {
		if (!isPendingToUpdate)
			handleGetDetailedCitizensBreastCancerProceduresIndicatorC7(
				pageNumberCitizensBreastCancerProcedures
			);
	}, [pageNumberCitizensBreastCancerProcedures]);

	useEffect(() => {
		if (!isPendingToUpdate) {
			if (pageNumberCitizensBreastCancerProcedures === 1) {
				handleGetDetailedCitizensBreastCancerProceduresIndicatorC7(
					pageNumberCitizensBreastCancerProcedures
				);
			} else {
				setPageNumberCitizensBreastCancerProcedures(1);
			}
		}
	}, [debouncedSearchCitizensBreastCancerProcedure, isEvaluatedCitizensBreastCancerProcedure]);

	useEffect(() => {
		if (isPendingToUpdate) handleFilter();
	}, [isPendingToUpdate]);

	useEffect(() => {
		setTitle("C7 - Cuidado da mulher na prevenção do câncer");
	}, [setTitle]);

	return (
		<>
			<IndicatorEsfC7Resume resume={resumeIndicatorC7} />
			<IndicatorEsfC7HealthTeams
				paginatedHealthTeamsClassifications={paginatedHealthTeamsClassifications}
				isLoading={isLoadingHealthTeamsClassifications}
				search={searchHealthTeamsClassification}
				setSearch={setSearchHealthTeamsClassification}
				selectedQuarter={selectedQuarter}
				pageNumber={pageNumberHealthTeamsClassifications}
				setPageNumber={setPageNumberHealthTeamsClassifications}
				handleGetAllResumeIndicatorC7ByHealthTeam={
					handleGetAllResumeIndicatorC7ByHealthTeam
				}
			/>
			<IndicatorEsfC7CitizensCervicalCancerProcedures
				paginatedProcedures={paginatedCitizensCervicalCancerProcedures}
				isLoading={isLoadingCitizensCervicalCancerProcedures}
				search={searchCitizensCervicalCancerProcedure}
				setSearch={setSearchCitizensCervicalCancerProcedure}
				isEvaluated={isEvaluatedCitizensCervicalCancerProcedure}
				setIsEvaluated={setIsEvaluatedCitizensCervicalCancerProcedure}
				selectedQuarter={selectedQuarter}
				pageNumber={pageNumberCitizensCervicalCancerProcedures}
				setPageNumber={setPageNumberCitizensCervicalCancerProcedures}
				handleGetAllDetailedIndicatorC7={
					handleGetAllDetailedCitizensCervicalCancerProceduresIndicatorC7
				}
			/>
			<IndicatorEsfC7CitizensVaccinations
				paginatedCitizensVaccinations={paginatedCitizensVaccinations}
				isLoading={isLoadingCitizensVaccinations}
				search={searchCitizensVaccination}
				setSearch={setSearchCitizensVaccination}
				isEvaluated={isEvaluatedCitizensVaccination}
				setIsEvaluated={setIsEvaluatedCitizensVaccination}
				selectedQuarter={selectedQuarter}
				pageNumber={pageNumberCitizensVaccinations}
				setPageNumber={setPageNumberCitizensVaccinations}
				handleGetAllDetailedCitizensVaccinationsIndicatorC7={
					handleGetAllDetailedCitizensVaccinationsIndicatorC7
				}
			/>
			<IndicatorEsfC7CitizensAttendances
				paginatedAttendances={paginatedCitizensWithAttendances}
				isLoading={isLoadingAttendances}
				search={searchCitizensWithAttendance}
				setSearch={setSearchCitizensWithAttendance}
				isEvaluated={isEvaluatedCitizensWithAttendance}
				setIsEvaluated={setIsEvaluatedCitizensWithAttendance}
				selectedQuarter={selectedQuarter}
				pageNumber={pageNumberAttendances}
				setPageNumber={setPageNumberAttendances}
				handleGetAllDetailedIndicatorC7={
					handleGetAllDetailedCitizensWithAttendancesIndicatorC7
				}
			/>
			<IndicatorEsfC7CitizensBreastCancerProcedures
				paginatedProcedures={paginatedCitizensBreastCancerProcedures}
				isLoading={isLoadingCitizensBreastCancerProcedures}
				search={searchCitizensBreastCancerProcedure}
				setSearch={setSearchCitizensBreastCancerProcedure}
				isEvaluated={isEvaluatedCitizensBreastCancerProcedure}
				setIsEvaluated={setIsEvaluatedCitizensBreastCancerProcedure}
				selectedQuarter={selectedQuarter}
				pageNumber={pageNumberCitizensBreastCancerProcedures}
				setPageNumber={setPageNumberCitizensBreastCancerProcedures}
				handleGetAllDetailedIndicatorC7={
					handleGetAllDetailedCitizensBreastCancerProceduresIndicatorC7
				}
			/>
		</>
	);
}
